import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

const ThreeViewerContext = createContext(null);

export function ThreeViewerProvider({ children }) {
  const [meshGroups, setMeshGroups] = useState([]);
  const [selectedMeshId, setSelectedMeshIdState] = useState(null);
  const [focusVersion, setFocusVersion] = useState(0);
  const [resetVersion, setResetVersion] = useState(0);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [gridSize, setGridSize] = useState(50);
  const [highlightedGroupIndices, setHighlightedGroupIndices] = useState(
    () => new Set()
  );
  const meshesRef = useRef([]);
  const cameraMotionRef = useRef(null);

  const handleMeshGroupsFound = useCallback(groups => {
    meshesRef.current = groups;
    setMeshGroups(groups);
    setIsModelLoaded(true);
  }, []);

  const handleBoundsCalculated = useCallback(bounds => {
    setGridSize(Math.max(bounds.maxDim * 1.5, 1));
  }, [])

  const setSelectedMeshId = useCallback(id => {
    setSelectedMeshIdState(id);
    setFocusVersion(version => version + 1);
  }, []);

  const toggleGroupHighlight = useCallback(index => {
    setHighlightedGroupIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, [])

  const resetView = useCallback(() => {
    setSelectedMeshIdState(null)
    setHighlightedGroupIndices(new Set());
    setResetVersion(version => version + 1);
  }, [])


  return (
    <ThreeViewerContext.Provider
      value={{
        meshGroups,
        selectedMeshId,
        focusVersion,
        resetVersion,
        isModelLoaded,
        gridSize,
        highlightedGroupIndices,
        meshesRef,
        cameraMotionRef,
        setSelectedMeshId,
        handleMeshGroupsFound,
        handleBoundsCalculated,
        toggleGroupHighlight,
        resetView,
      }}>
      {children}
    </ThreeViewerContext.Provider>
  );
}

export function useThreeViewer() {
  const context = useContext(ThreeViewerContext);
  if (!context) {
    throw new Error('useThreeViewer must be used within ThreeViewerProvider');
  }
  return context;
}